import { Button, Flex, Spinner, Text, Image } from '@chakra-ui/react';
import styled from '@emotion/styled';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, useMemo } from 'react';
import PlayIcon from './PlayIcon';
import PauseIcon from './PauseIcon';
import Skipicon from './SkipIcon';
import FullscreenIcon from './FullScreenIcon';
import ShrinkIcon from './ShrinkScreenIcon';
import ElapsedTimeTracker from './ElapsedTimeTracker';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { VolumeControl } from '../PlayerComponents/VolumeControl';
import { VideoSetting } from '../PlayerComponents/VideoSettings';
import SettingsIcon from './SettingsIcon';
import './player.css';
import { IProject, TComment, TResolutions } from '../../../types/global/helper';
import { ICreateView } from '../../../redux/view/types';
import { ShareModal } from '../../ShareModal/ShareModal';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
    deleteLike,
    downloadProject,
    getProjectComments,
    leaveComment,
    likeProject,
    updateComment,
} from '../../../redux/project/actions';
import { en_config } from '../../../config';
import {
    isUserEditorSelector,
    likeActionInProgressSelector,
    nebulaMuteSelector,
    nebulaQualitySelector,
    nebulaSpeedSelector,
    nebulaVolumeSelector,
    shareModalStatusSelector,
    userProjectInteractionSelector,
} from '../../../selectors/selectors';
import { ILike, TLikeOrigin } from '../../../redux/account/types';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { SentryCapture } from '../../../analytics/Sentry';
import { MetaTags } from '../../Metatags/Metatags';
import { setMute, setPlayerQuality, setPlayerSpeed, setPlayerVolume } from '../../../redux/player/action';
import { InformationDisplay } from './InformationDisplay';
import { generateVideoSnapshot } from '../VideoSnapshotGenerator';
import { catchAsync, useDispatchPromise } from '../../../utils/AppUtils';
import { EsPlayButton } from '../../EsButtons/EsPlayButton';
import { SITE_URL } from '../../../configurations/api/url';
import PictureInPictureIcon from '../../CustomSVGIcons/PictureInPictureIcon';
import TheatreModeIcon from '../../CustomSVGIcons/TheatreModeIcon';
import ReplayIcon from './ReplayIcon';
import { clearCache } from '../../../redux/cache/action';
import { CACHE_TYPE } from '../../../redux/cache/types';
import { setShareModalStatus } from '../../../redux/appConfigurator/action';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
//@ts-ignore
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { PlaylistMenu } from '../../More/PlaylistMenu';
import { playlist_menu } from '../../More/more.data';

const Video = styled.video`
    flex-shrink: 1;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
`;

const VIEW_COUNT_IN_SECONDS = 3;

export interface IPlayerProps {
    src: string;
    muted?: boolean;
    autoPlay?: boolean;
    project?: IProject;
    onViewUpdate?: (payload: ICreateView) => void;
    onNextPress?: () => void;
    videoStyle?: React.CSSProperties;
    loading?: boolean;
    commentOpen?: boolean;
    onTogleFullScreen?: (isFullScreen: boolean) => void;
    onEnded?: () => void;
    loop?: boolean;
    endlessPlayer?: boolean;
    persistInline?: boolean;
    volumeControl?: number;
    onTheatreModePressed?: () => void;
    onLoadFailure?: () => void;
    isEditMode?: boolean;
    useProjectUrl?: boolean; // New prop to default to project.project URL
    customPoster?: string;
}

export interface INebulaPlayerRef {
    handleNextClick(): void;
    handleVoteClick(): void;
    handleFetchProjectComments(): void;
    handleLikeProject(like_origin: TLikeOrigin, isAlreadyLiked?: boolean, like?: ILike): void;
    handleFetchProjectComments(): void;
    handleViewUpdate(): void;
    handleShareClick(): void;
    handleCommentSend(commentText: string, comment?: TComment, isReply?: boolean, isEdit?: boolean): void;
    handlePlayerTracker(): void;
    handleSnapshot(): Promise<string>;
    handleDownload(): void;
}

const Player = React.forwardRef<INebulaPlayerRef, IPlayerProps>((props: IPlayerProps, ref) => {
    const {
        src,
        autoPlay,
        project,
        onViewUpdate,
        onNextPress,
        videoStyle,
        commentOpen,
        onTogleFullScreen,
        onEnded,
        loop,
        endlessPlayer,
        volumeControl,
        onTheatreModePressed,
        persistInline,
        onLoadFailure,
        isEditMode,
        useProjectUrl,
        customPoster,
    } = props;

    const systenVolume = useSelector(nebulaVolumeSelector);
    const systemQuality = useSelector(nebulaQualitySelector);
    const systemSpeed = useSelector(nebulaSpeedSelector);
    const systemMute = useSelector(nebulaMuteSelector);

    const [isWaiting, setIsWaiting] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [settingsPosition, setSettingsPosition] = useState({ x: 0, y: 0 });
    const [error, setError] = useState(false);
    const [isPlaying, setIsPlaying] = useState(autoPlay || false);
    const [isDragging, setIsDragging] = useState(false);
    const [isPaused, setIsPaused] = useState(!autoPlay);
    const [lastTime, setLastTime] = useState<number>(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [durationSec, setDurationSec] = useState(1);
    const [elapsedSec, setElapsedSec] = useState(1);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [volume, setVolume] = useState(volumeControl ?? systenVolume);
    const [showSettings, setShowSettings] = useState(false);
    const [showControls, setShowControls] = useState(false);
    const [loadTimeout, setLoadTimeout] = useState(false);
    const { themeStyle, device } = useThemeContext();
    const hasEnded = durationSec === elapsedSec;

    const videoRef = useRef<HTMLVideoElement>(null);
    const progressRef = useRef<HTMLDivElement>(null);
    const bufferRef = useRef<HTMLDivElement>(null);
    const lastUpdateTimeRef = useRef<number>(0);
    const settingsAchorRef = useRef<any>(null);
    const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [availableResolutions, setAvailableResolutions] = useState<TResolutions>();
    const [selectedResolution, setSelectedResolution] = useState('auto');
    const [hasFallenBackToProjectUrl, setHasFallenBackToProjectUrl] = useState(false);
    const progressPositionBeforeResolutionChange = useRef<any>(0);
    const { is_user_liked, userLike } = useSelector(userProjectInteractionSelector);
    const likingInProgress = useSelector(likeActionInProgressSelector);
    const showShareModal = useSelector(shareModalStatusSelector);
    const isUserEditor = useSelector(isUserEditorSelector);
    // Determine the video source based on props and fallback state
    const selectedSource = useMemo(() => {
        // If useProjectUrl is true or we've fallen back to project URL, use project.project
        if (useProjectUrl || hasFallenBackToProjectUrl) {
            return project?.project || src;
        }

        // Otherwise, try resolutions first, then fallback to project.project
        if (project && project?.resolutions && availableResolutions) {
            const resolutionUrl = availableResolutions[selectedResolution as keyof TResolutions];
            return resolutionUrl || project.project || src;
        }

        return project?.project || src;
    }, [project, useProjectUrl, hasFallenBackToProjectUrl, availableResolutions, selectedResolution, src]);
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        setIsPlaying(false);
        setIsPaused(true);
        setIsBuffering(false);
        setIsWaiting(true);
        setElapsedSec(0);
        setLastTime(0);

        video.pause();
        video.removeAttribute('src');
        video.load();

        setTimeout(() => {
            video.src = selectedSource;
            video.load();

            if (autoPlay) {
                video
                    .play()
                    .then(() => {
                        setIsPlaying(true);
                        setIsPaused(false);
                        setError(false);
                    })
                    .catch(err => {
                        console.error('Autoplay failed', err);
                        SentryCapture(err, 'error');
                        setError(true);
                    });
            }
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSource]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        // Reset all player states
        setIsPlaying(false);
        setIsPaused(true);
        setIsBuffering(false);
        setIsWaiting(true);
        setElapsedSec(0);
        setLastTime(0);
        setError(false);
        setLoadTimeout(false);

        // Force reset the video element
        video.pause();
        video.removeAttribute('src');
        video.load();

        // Clear any existing timeouts
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }

        setTimeout(() => {
            if (video && selectedSource) {
                video.src = selectedSource;
                video.load();

                if (autoPlay) {
                    video
                        .play()
                        .then(() => {
                            setIsPlaying(true);
                            setIsPaused(false);
                            setError(false);
                        })
                        .catch(err => {
                            console.error('Autoplay failed', err);
                            SentryCapture(err, 'error');
                            setError(true);
                        });
                }
            }
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSource, project?.project]);

    const handlOnLoad = () => {
        setIsLoading(false);
        setIsBuffering(false);
        setError(false);
    };

    /**
     * Making The decision to handle all necessary service calls
     * for this player inside this component, this allows for
     * less duplicate code everywhere else we use the Nebula Player
     */
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();

    const handleRefresh = () => {
        // Destroy the project cache and refresh again
        if (project && project.video_id) {
            dispatch(
                clearCache({
                    cacheType: CACHE_TYPE.PROJECT,
                    key: project?.video_id,
                }),
            );
        }
        setError(false);
        window.location.reload();
    };

    const handleCanPlay = () => {
        setIsLoading(false);
        setError(false);
    };

    const setVideoQualityBasedOnConnection = useCallback(() => {
        if ('connection' in navigator) {
            const connection: any = navigator.connection;
            const connectionType = connection.effectiveType; // Get connection type (slow-2g, 3g, 4g, etc.)
            const downlink = connection.downlink; // Get downlink speed estimate (in Mbps)
            // Determine video quality based on connection type and downlink speed
            let selectedQuality = 'auto'; // Default quality (let the browser choose)

            if (connectionType === 'wifi' || connectionType === '4g') {
                // Higher quality for WiFi or 4G connections TODO: Add 1080p when avilable
                selectedQuality = '720p'; // Set to the highest available quality
            } else if (connectionType === '3g' || downlink < 2.0) {
                // Lower quality for slower connections (3G or below 2 Mbps)
                selectedQuality = '480p'; // Set to a lower quality
            } else if (downlink < 1.0) {
                // Very slow connections (below 1 Mbps)
                selectedQuality = '360p'; // Set to the lowest quality
            }

            // Set the video quality based on the determined level
            handleQualityChange(selectedQuality);
        } else {
            // Probably mobile scren or Safari
            handleQualityChange('720p');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMuteControl = useCallback((status: boolean) => dispatch(setMute(status)), [dispatch]);

    // Function to handle quality change
    const handleQualityChange = (quality: string) => {
        progressPositionBeforeResolutionChange.current = elapsedSec;
        if (quality === 'auto') {
            setVideoQualityBasedOnConnection();
        } else {
            setSelectedResolution(quality);
            dispatch(setPlayerQuality(quality));
        }
    };

    /**
     *
     * Checks when a video is stuck for any reaso
     * This approach is a bit tacky but we can revisit in the future
     */
    const checkStuck = useCallback(() => {
        if (error || lastTime === durationSec || isPaused) {
            return;
        }
        if (videoRef.current) {
            const currentTime = videoRef.current.currentTime;
            if (lastTime === currentTime) {
            } else {
                setLastTime(currentTime);
            }
        }
    }, [lastTime, durationSec, error, isPaused]);

    useEffect(() => {
        const checkInterval = setInterval(checkStuck, 2000); // Check every 2 seconds
        return () => {
            clearInterval(checkInterval);
        };
    }, [checkStuck]);

    const handleResolution = useCallback(() => {
        if (project?.resolutions) {
            setAvailableResolutions(project.resolutions);
            setVideoQualityBasedOnConnection();
        }
    }, [project, setVideoQualityBasedOnConnection]);

    const handleError = useCallback(
        (e: any) => {
            const videoElement = e.currentTarget;
            const error = videoElement.error;

            // Check if we're currently using resolutions and haven't fallen back yet
            const isUsingResolutions =
                project?.resolutions && availableResolutions && !useProjectUrl && !hasFallenBackToProjectUrl;

            // Check for specific media errors that indicate format/codec issues
            if (error && error.message) {
                const errorMessage = error.message;
                SentryCapture(errorMessage, 'error');

                if (
                    error.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ||
                    error.code === MediaError.MEDIA_ERR_DECODE
                ) {
                    setLoadTimeout(true);
                    console.error('Media format not supported:', errorMessage);
                }
            } else {
                SentryCapture('Unknown error', 'error');
            }

            // If we're using resolutions and they failed, try falling back to project.project
            if (isUsingResolutions && project?.project) {
                console.log('Resolution failed, falling back to project.project URL');
                setHasFallenBackToProjectUrl(true);
                setError(false);
                setIsLoading(true);

                // Reset the video element and try with project.project
                if (videoRef.current) {
                    videoRef.current.pause();
                    videoRef.current.removeAttribute('src');
                    videoRef.current.load();

                    setTimeout(() => {
                        if (videoRef.current) {
                            videoRef.current.src = project.project;
                            videoRef.current.load();

                            if (autoPlay) {
                                videoRef.current.play().catch(err => {
                                    console.error('Fallback autoplay failed:', err);
                                    setError(true);
                                    setIsLoading(false);
                                    if (onLoadFailure) onLoadFailure();
                                });
                            }
                        }
                    }, 100);
                }
                return; // Don't call onLoadFailure yet, let the fallback try
            }

            setIsWaiting(false);
            setError(true);
            setIsLoading(false);

            // Call onLoadFailure callback if provided
            if (onLoadFailure) {
                onLoadFailure();
            }
        },
        [onLoadFailure, project, availableResolutions, useProjectUrl, hasFallenBackToProjectUrl, autoPlay],
    );

    // Reset fallback state immediately when project changes
    useEffect(() => {
        setHasFallenBackToProjectUrl(false);
    }, [project?._id]);

    useEffect(() => {
        handleResolution();
        return () => {
            // handleMuteControl(true); Removing to handle swipe better
        };
    }, [handleResolution, project]);

    useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        const onWaiting = () => {
            setIsWaiting(true);
            setIsBuffering(true);
        };

        const onPlay = () => {
            if (isWaiting) setIsWaiting(false);
            // Always update isPlaying state when play event occurs
            setIsPlaying(true);
            setIsPaused(false);
            setLoadTimeout(false); // Clear timeout flag if playback starts

            if (videoRef.current) {
                videoRef.current
                    .play()
                    .then(() => {
                        setIsLoading(false);
                        setIsBuffering(false);
                        setError(false); // Clear error state if play succeeds
                        // Show controls when playback starts
                        setShowControls(true);
                        // Hide controls after a delay if playing
                        if (controlsTimeoutRef.current) {
                            clearTimeout(controlsTimeoutRef.current);
                        }
                        controlsTimeoutRef.current = setTimeout(() => {
                            setShowControls(false);
                        }, 1500);
                    })
                    .catch(e => {
                        SentryCapture(e, 'info');
                        // Check for specific error types related to media being non-playable
                        if (e.name === 'NotSupportedError' || e.name === 'MediaError') {
                            setLoadTimeout(true); // Reuse loadTimeout for unsupported media
                            setError(true);
                            setIsPlaying(false);
                            setIsPaused(true);
                            console.error('Media not supported or playable:', e);
                        }
                    });
            }
        };

        const onPause = () => {
            if (videoRef.current) videoRef.current.pause();
            setIsPlaying(false);
            setIsPaused(true);
            setShowControls(true); // Always show controls when paused
            setIsWaiting(false);
            handleOnViewUpdate(elapsedSec);
        };

        const onLoadedData = () => {
            setIsLoading(false);
        };

        const onLoadedMetaData = () => {
            setIsLoading(false);
            setIsBuffering(false);
        };

        const handleOnViewUpdate = (time: number) => {
            if (onViewUpdate && project && !isDragging) {
                onViewUpdate({
                    duration_watched: time,
                    view_type: 'project',
                    projectId: project?._id,
                });
            }
        };

        const element = videoRef.current;

        const onProgress = () => {
            if (!element.buffered || !bufferRef.current) return;
            if (!element.buffered.length) return;
            const bufferedEnd = element.buffered.end(element.buffered.length - 1);
            const duration = element.duration;
            if (bufferRef && duration > 0) {
                bufferRef.current.style.width = (bufferedEnd / duration) * 100 + '%';
            }
        };

        const onTimeUpdate = () => {
            setIsWaiting(false);
            if (!element.buffered || !progressRef.current) return;
            const duration = element.duration;
            setDurationSec(duration);
            setElapsedSec(element.currentTime);
            /** handle Updating the watch time for project */
            if (element.currentTime - lastUpdateTimeRef.current >= VIEW_COUNT_IN_SECONDS) {
                lastUpdateTimeRef.current = element.currentTime;
                handleOnViewUpdate(element.currentTime);
            }
            if (progressRef && duration > 0) {
                progressRef.current.style.width = (element.currentTime / duration) * 100 + '%';
            }
        };

        element.addEventListener('progress', onProgress);
        element.addEventListener('timeupdate', onTimeUpdate);

        element.addEventListener('waiting', onWaiting);
        element.addEventListener('play', () => {
            onPlay();
        });
        element.addEventListener('playing', () => {
            onPlay();
        });
        element.addEventListener('pause', onPause);
        element.addEventListener('loadedmetadata', onLoadedMetaData);
        element.addEventListener('loadeddata', onLoadedData);
        element.addEventListener('canplaythrough', handleCanPlay);
        element.addEventListener('error', handleError);

        // Add a specific event listener for autoplay detection
        if (videoRef.current && autoPlay) {
            // Check if the video is already playing (could happen with autoplay)
            const checkAutoplayState = () => {
                if (videoRef.current && !videoRef.current.paused) {
                    setIsPlaying(true);
                    setIsPaused(false);
                    setShowControls(true);
                    // Hide controls after a delay if playing
                    if (controlsTimeoutRef.current) {
                        clearTimeout(controlsTimeoutRef.current);
                    }
                    controlsTimeoutRef.current = setTimeout(() => {
                        setShowControls(false);
                    }, 1500);
                }
            };

            // Check immediately and after a short delay (to catch autoplay)
            checkAutoplayState();
            const autoplayCheckTimeout = setTimeout(checkAutoplayState, 500);

            return () => {
                clearTimeout(autoplayCheckTimeout);
            };
        }

        // clean up
        return () => {
            element.removeEventListener('waiting', onWaiting);
            element.removeEventListener('play', onPlay);
            element.removeEventListener('playing', onPlay);
            element.removeEventListener('pause', onPause);
            element.removeEventListener('progress', onProgress);
            element.removeEventListener('timeupdate', onTimeUpdate);
            element.removeEventListener('loadedmetadata', onLoadedMetaData);
            element.removeEventListener('loadeddata', onLoadedData);
            setIsPlaying(false);
            setIsLoading(false);
            setIsBuffering(false);
        }; // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoRef.current, autoPlay]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const onPlay = () => {
            setIsPlaying(true);
            setIsPaused(false);
            setError(false);
        };

        const onPause = () => {
            setIsPlaying(false);
            setIsPaused(true);
        };

        const onError = (e: Event) => {
            setError(true);
            setIsPlaying(false);
            console.error('Video Error', video.error);
            SentryCapture(video.error, 'error');
            onLoadFailure?.();
        };

        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPause);
        video.addEventListener('error', onError);

        return () => {
            video.removeEventListener('play', onPlay);
            video.removeEventListener('pause', onPause);
            video.removeEventListener('error', onError);
        };
    }, [onLoadFailure]);

    // This is where the playback rate is set on the video element.
    useEffect(() => {
        if (!videoRef.current) return;
        if (videoRef.current.playbackRate === playbackRate) return;
        videoRef.current.playbackRate = playbackRate;
    }, [playbackRate]);

    useEffect(() => {
        if (!videoRef.current) return;
        if (videoRef.current.volume === volume) return;
        videoRef.current.volume = volume;
    }, [volume]);

    useEffect(() => {
        if (systemQuality) {
            setSelectedResolution(systemQuality);
        }

        if (systenVolume) {
            setVolume(systenVolume);
        }

        if (systemSpeed) {
            setPlaybackRate(systemSpeed);
        }
    }, [systemQuality, systenVolume, systemSpeed]);

    useEffect(() => {
        document.addEventListener('fullscreenchange', handleFullScreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Add timeout detection for videos that don't load or play properly
    useEffect(() => {
        let timeoutTimer: NodeJS.Timeout | null = null;

        if (selectedSource && isLoading && !isPlaying && !error) {
            // Set a timeout to show error if video doesn't load within 10 seconds
            timeoutTimer = setTimeout(() => {
                setLoadTimeout(true);
                setError(true);
                setIsLoading(false);
                console.error('Video load timed out:', selectedSource);
                if (onLoadFailure) onLoadFailure();
            }, 10000);
        }

        // Clear timeout if video starts playing or on unmount
        return () => {
            if (timeoutTimer) clearTimeout(timeoutTimer);
        };
    }, [selectedSource, isLoading, isPlaying, error, onLoadFailure]);

    const seekToPosition = useCallback((pos: number) => {
        if (!videoRef.current) return;
        if (pos < 0 || pos > 1) return;

        const durationMs = videoRef.current.duration * 1000 || 0;

        const newElapsedMs = durationMs * pos;
        const newTimeSec = newElapsedMs / 1000;
        handleSeekToTime(newTimeSec);
    }, []);

    useEffect(() => {
        if (
            project &&
            project.views &&
            videoRef &&
            videoRef.current &&
            project.views.duration_watched &&
            project.views.duration_watched - videoRef.current.duration < 5
        ) {
            handleSeekToTime(project.views.duration_watched);
        } else {
            seekToPosition(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoRef, project]);

    useEffect(() => {
        if (progressPositionBeforeResolutionChange.current > 0 && videoRef.current) {
            seekToPosition(progressPositionBeforeResolutionChange.current);
            handleSeekToTime(progressPositionBeforeResolutionChange.current);
            progressPositionBeforeResolutionChange.current = 0;
        }
    }, [selectedResolution, seekToPosition]);

    useEffect(() => {
        const updatePosition = () => {
            if (settingsAchorRef.current) {
                const rect = settingsAchorRef.current.getBoundingClientRect();
                setSettingsPosition({ x: rect.left, y: rect.top });
            }
        };
        updatePosition();
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('resize', updatePosition);
        };
    }, []);

    // Modify the handleControlsVisibility function to consider isPlaying state
    const handleControlsVisibility = useCallback(() => {
        // Clear any existing timeout
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }

        // Show controls
        setShowControls(true);

        // Set a new timeout to hide controls after inactivity (reduced to 1.5 seconds)
        // Only hide controls when playing
        if (isPlaying) {
            controlsTimeoutRef.current = setTimeout(() => {
                setShowControls(false);
            }, 1500);
        }
    }, [isPlaying]);

    useEffect(() => {
        // Clean up timeout on unmount
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
        };
    }, []);

    // Add a playback watchdog timer to detect and recover from stuck videos
    useEffect(() => {
        let watchdogTimer: NodeJS.Timeout | null = null;

        // Start a watchdog timer when video is supposed to be playing
        const startWatchdog = () => {
            // Clear any existing timer
            if (watchdogTimer) {
                clearTimeout(watchdogTimer);
            }

            // Set a new timer that checks if video is progressing
            watchdogTimer = setTimeout(() => {
                if (videoRef.current && isPlaying && !isBuffering && !isWaiting) {
                    const currentTime = videoRef.current.currentTime;

                    // If video hasn't advanced in 3 seconds but should be playing
                    if (currentTime === lastTime && !isPaused) {
                        console.log('Video playback appears stuck, attempting recovery');

                        // Try to recover by seeking slightly forward
                        try {
                            // Save volume level
                            const currentVolume = videoRef.current.volume;
                            const wasMuted = videoRef.current.muted;

                            // Force reload the player
                            videoRef.current.pause();
                            videoRef.current.currentTime = currentTime + 0.1;

                            // Try playing again with a delay
                            setTimeout(() => {
                                if (videoRef.current) {
                                    videoRef.current.volume = currentVolume;
                                    videoRef.current.muted = wasMuted;

                                    videoRef.current
                                        .play()
                                        .then(() => {
                                            console.log('Successfully recovered playback');
                                            setIsPlaying(true);
                                            setIsPaused(false);
                                        })
                                        .catch(error => {
                                            console.error('Recovery attempt failed:', error);
                                            SentryCapture(`Video recovery error: ${error.message}`, 'error');

                                            // Last resort - try one more time with user interaction simulation
                                            setTimeout(() => {
                                                if (videoRef.current) {
                                                    videoRef.current.play().catch(e => {
                                                        console.error('Final recovery attempt failed');
                                                        // Show error UI or message to user
                                                        setError(true);
                                                    });
                                                }
                                            }, 500);
                                        });
                                }
                            }, 100);
                        } catch (e) {
                            console.error('Error during playback recovery:', e);
                        }
                    }
                }

                // Restart the watchdog
                startWatchdog();
            }, 3000); // Check every 3 seconds
        };

        // Start watchdog if video is playing
        if (isPlaying && !isPaused) {
            startWatchdog();
        }

        // Cleanup on unmount
        return () => {
            if (watchdogTimer) {
                clearTimeout(watchdogTimer);
            }
        };
    }, [isPlaying, isPaused, isBuffering, isWaiting, lastTime]);

    // Modify the handlePlayPauseClick function to make it more robust
    const handlePlayPauseClick = (e?: React.MouseEvent) => {
        // Prevent event bubbling if provided
        if (e) {
            e.stopPropagation();
        }

        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
                setIsPaused(true);
                setIsPlaying(false);
                setShowControls(true); // Always show controls when paused
            } else {
                // First make sure video element is ready
                if (videoRef.current.readyState === 0) {
                    console.log('Video not ready yet, loading...');
                    setIsBuffering(true);
                    videoRef.current.load();

                    // Set a listener for when it's loaded enough to play
                    const canPlayHandler = () => {
                        startPlayback();
                        videoRef.current?.removeEventListener('canplay', canPlayHandler);
                    };

                    videoRef.current.addEventListener('canplay', canPlayHandler);
                } else {
                    // Video is ready, try playing immediately
                    startPlayback();
                }
            }
            handleMuteControl(false);
        }
    };

    // Extract play logic to reuse it
    const startPlayback = () => {
        if (!videoRef.current) return;

        // Try playing with retry logic
        const attemptPlay = (retries = 3) => {
            videoRef.current
                ?.play()
                .then(() => {
                    setIsPaused(false);
                    setIsPlaying(true);
                    setError(false);
                    handleControlsVisibility(); // Update controls visibility timer
                })
                .catch(error => {
                    console.error('Play error:', error);

                    if (retries > 0) {
                        console.log(`Retrying playback, ${retries} attempts left`);
                        setTimeout(() => attemptPlay(retries - 1), 300);
                    } else {
                        SentryCapture(`Video play error after retries: ${error.message}`, 'error');
                        setIsPlaying(false);
                        setIsPaused(true);
                        setShowControls(true); // Show controls on error

                        // Show visual feedback that play failed
                        setError(true);

                        // Auto-retry once more after a longer delay
                        setTimeout(() => {
                            if (videoRef.current) {
                                videoRef.current.play().catch(() => {
                                    // Silent catch - we've already shown error feedback
                                });
                            }
                        }, 2000);
                    }
                });
        };

        attemptPlay();
    };

    const handleReplay = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play().then(e => {
                setIsPlaying(true);
                setIsPaused(false);
                setError(false);
            });
        }
    };
    const handleSkipPress = () => {
        if (onNextPress) {
            onNextPress();
        }
    };

    /** handles seeking to time  !! DO NOT REMOVE */
    const handleSeekToTime = (time: number) => {
        if (videoRef.current) {
            videoRef.current.currentTime = time;
        }
    };

    const handleVolumeChange = (event: any) => {
        if (videoRef.current) {
            const volumeValue = event.target.value;
            videoRef.current.volume = volumeValue;
            setVolume(videoRef.current.volume);
            dispatch(setPlayerVolume(videoRef.current.volume));
        }
    };

    const handleFullScreenToggle = () => {
        const div = document.getElementById('wrapper');
        if (div) {
            if (!document.fullscreenElement) {
                div.requestFullscreen().catch(e => console.log(e));
                if (onTogleFullScreen) onTogleFullScreen(true);
            } else {
                document.exitFullscreen();
                if (onTogleFullScreen) onTogleFullScreen(false);
            }
        }
    };
    const handlePipToggle = () => {
        const videoElement = videoRef.current;

        if (videoElement && document.pictureInPictureEnabled && !document.pictureInPictureElement) {
            videoElement.requestPictureInPicture().catch(error => {
                console.error('Error trying to enter PiP mode:', error);
            });
        } else {
            document.exitPictureInPicture().catch(error => {
                console.error('Error trying to exit PiP mode:', error);
            });
        }
    };
    const handleToggleSettings = () => {
        setShowSettings(!showSettings);
    };

    const handleFullScreenChange = () => {
        const isInFullScreen = !!document.fullscreenElement;
        setIsFullScreen(isInFullScreen);
        handleControlsVisibility();
    };

    useEffect(() => {
        // Add mousemove event listener to the fullscreen element
        const videoWrapper = document.getElementById('wrapper');
        if (videoWrapper) {
            videoWrapper.addEventListener('mousemove', handleControlsVisibility);
        }

        return () => {
            if (videoWrapper) {
                videoWrapper.removeEventListener('mousemove', handleControlsVisibility);
            }
        };
    }, [handleControlsVisibility]);

    /**
     * Imperative Methods
     * Handle Next
     * Handle Vote
     * Handle Fetch Comments
     * Handle Share
     * Handle Update View
     * Handle Like
     */

    const handleNextClick = () => {};

    const handleVoteClick = () => {};

    const handleFetchProjectComments = () => {
        if (project && project._id)
            dispatch(
                getProjectComments({
                    params: `?project=${project._id}&limit=${en_config.RESULT_LIMIT}&page=1&sort=-createdAt`,
                }),
            );
    };

    const handleLikeProject = (like_origin: TLikeOrigin, isAlreadyLiked?: boolean, like?: ILike) => {
        if (!likingInProgress) {
            if (project) {
                if (is_user_liked && userLike) {
                    dispatch(
                        deleteLike({
                            documentId: userLike[0]._id,
                            payload: like_origin,
                            additionalPayload: userLike[0].project,
                        }),
                    );
                } else {
                    dispatch(
                        likeProject({
                            like_category: 'project',
                            like_type: 'like',
                            projectId: project?._id as string,
                            like_origin: like_origin,
                        }),
                    );
                }
            }
        } else {
            console.log('Like in progress');
        }
    };

    const handleViewUpdate = () => {
        console.log('View Update here');
    };

    const handleShareClick = () => {
        dispatch(
            setShareModalStatus({
                status: true,
                url: `${SITE_URL}/watch-share?id=${project?.video_id}`,
                title: project?.title,
            }),
        );
    };

    const getAndSetProjectDetails = () => {
        batch(() => {
            handleFetchProjectComments();
        });
    };

    const handleCommentSend = (text: string, comment?: TComment, isReply?: boolean, isEdit?: boolean) => {
        if (isEdit && comment) {
            dispatch(
                updateComment({
                    documentId: comment?._id,
                    payload: text,
                }),
            );
        } else {
            dispatch(
                leaveComment({
                    comment: text,
                    project: project as any,
                    commentId: comment?._id,
                    commentType: 'project',
                    isReply: isReply,
                    parentComment: comment,
                }),
            );
        }
    };

    const handlePlayerTracker = () => isPlaying;

    const handleSnapshot = async () => {
        if (project && project.project && isPaused) {
            const snapshotDataUrl = await generateVideoSnapshot(project?.project, durationSec);
            return snapshotDataUrl;
        }
        return '';
    };

    const handleDownload = catchAsync(async () => {
        if (project?.download_link && project.downloadable) {
            /** Download the clear version */
            const selectedLink = isUserEditor ? project.project : project?.download_link;
            const response: any = await dispatchPromise(downloadProject(selectedLink));
            const blob = await response.blob();
            const anchor = document.createElement('a');
            anchor.href = window.URL.createObjectURL(blob);
            anchor.download = `${project.title}.mp4`;
            anchor.click();
        } else {
            console.log('Download not supported');
        }
    });

    useImperativeHandle(ref, () => ({
        handleFetchProjectComments: handleFetchProjectComments,
        handleLikeProject: handleLikeProject,
        handleNextClick: handleNextClick,
        handleViewUpdate: handleViewUpdate,
        handleVoteClick: handleVoteClick,
        handleShareClick: handleShareClick,
        handleCommentSend: handleCommentSend,
        handlePlayerTracker: handlePlayerTracker,
        handleSnapshot: handleSnapshot,
        handleDownload: handleDownload,
    }));

    useEffect(() => {
        getAndSetProjectDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMouseDown = () => {
        setIsDragging(true);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!isDragging) return;
        const { left, width } = e.currentTarget.getBoundingClientRect();
        const clickedPos = (e.clientX - left) / width;
        seekToPosition(clickedPos);
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        setIsDragging(true);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!isDragging) return;
        const { left, width } = e.currentTarget.getBoundingClientRect();
        const touchMoveX = e.touches[0].clientX;
        const clickedPos = (touchMoveX - left) / width;
        seekToPosition(clickedPos);
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const customControl = (
        <Flex
            w="full"
            h="100px"
            bgGradient={'linear(to top, black, transparent)'}
            pos="absolute"
            opacity={showControls ? 1 : 0}
            zIndex={2}
            transition="opacity 0.3s ease-in-out"
            className="timeline-container custom-video-controls"
            left={0}
            bottom={0}
            align="flex-end"
            px="1rem">
            <Flex flexDir="column" w="full" align="center" opacity={showControls ? 1 : 0}>
                <Flex
                    w="full"
                    transition="height 0.1s linear"
                    className="timeline"
                    h={isDragging ? '15px' : '8px'}
                    mb="0.5rem"
                    rounded="3px"
                    bg="rgba(193, 193, 193, 0.5)"
                    _hover={{ height: '15px' }}
                    overflow="hidden"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                        const { left, width } = e.currentTarget.getBoundingClientRect();
                        const clickedPos = (e.clientX - left) / width;
                        seekToPosition(clickedPos);
                    }}>
                    <Flex pos="relative" w="full" h="full">
                        <Flex
                            h="full"
                            className="play-progress"
                            bg={theme.dark.highlight}
                            zIndex={1}
                            ref={progressRef}
                        />
                        <Flex
                            pos="absolute"
                            h="full"
                            className="buffer-progress"
                            bg="#FDFFFC"
                            ref={bufferRef}
                            transition={'width 0.3s ease'}
                        />
                    </Flex>
                </Flex>
                {(!endlessPlayer || persistInline) && (
                    <Flex w="full" justify="space-between" align="center">
                        <Flex align="center" w={'full'}>
                            <Button
                                maxW="30px"
                                minW="30px"
                                w="30px"
                                p="0"
                                mr="0.4rem"
                                maxH="25px"
                                h="30px"
                                rounded="4px"
                                colorScheme="transparent"
                                bg="transparent"
                                mb="0.5rem"
                                _hover={{
                                    bg: 'rgba(0, 0, 0, 0.4)',
                                }}>
                                {!isPlaying ? (
                                    hasEnded ? (
                                        <ReplayIcon onClick={handleReplay} height={18} width={18} />
                                    ) : (
                                        <PlayIcon onClick={handlePlayPauseClick} />
                                    )
                                ) : (
                                    <PauseIcon onClick={handlePlayPauseClick} />
                                )}
                            </Button>

                            {!endlessPlayer && !isEditMode && (
                                <Button
                                    maxW="30px"
                                    minW="30px"
                                    w="30px"
                                    p="0"
                                    mr="0.4rem"
                                    maxH="25px"
                                    h="30px"
                                    rounded="4px"
                                    colorScheme="transparent"
                                    bg="transparent"
                                    mb="0.5rem"
                                    _hover={{
                                        bg: 'rgba(0, 0, 0, 0.4)',
                                    }}
                                    onClick={handleSkipPress}>
                                    <Skipicon />
                                </Button>
                            )}
                            <ElapsedTimeTracker totalSec={durationSec} elapsedSec={elapsedSec} />
                            {!endlessPlayer && (
                                <VolumeControl
                                    handleVolume={handleVolumeChange}
                                    value={volume}
                                    isMute={systemMute}
                                    onTapVolumeIcon={() => {
                                        handleMuteControl(!systemMute);
                                    }}
                                />
                            )}
                        </Flex>

                        <Flex align="center">
                            <VideoSetting
                                showSettings={showSettings}
                                settingsSX={{
                                    bottom: settingsPosition.y + '50px',
                                    right: settingsPosition.x + '50px',
                                }}
                                onChangeOption={({ option, optionTitle }) => {
                                    if (optionTitle === 'playback speed') {
                                        setPlaybackRate(Number(option));
                                        dispatch(setPlayerSpeed(Number(option)));
                                    } else {
                                        /** Handle the quality selection  */
                                        // handleQualityChange(option);
                                        handleQualityChange(option);
                                    }
                                    setShowSettings(!showSettings);
                                }}
                            />
                            {!isEditMode && (
                                <Button
                                    maxW="20px"
                                    minW="20px"
                                    w="25px"
                                    p="0"
                                    mr="0.4rem"
                                    maxH="20px"
                                    h="25px"
                                    ml="0.4rem"
                                    rounded="4px"
                                    colorScheme="transparent"
                                    bg="transparent"
                                    mb="0.5rem"
                                    _hover={{
                                        bg: 'rgba(255, 255, 255, 0.08)',
                                    }}>
                                    <PlaylistMenu
                                        onClick={() => {}}
                                        menu={playlist_menu}
                                        project={project}
                                        iconStyle={{
                                            height: '20px',
                                            width: 'auto',
                                        }}
                                    />
                                </Button>
                            )}
                            <Button
                                maxW="20px"
                                minW="20px"
                                w="25px"
                                p="0"
                                mr="0.4rem"
                                maxH="20px"
                                h="25px"
                                ml="0.4rem"
                                rounded="4px"
                                colorScheme="transparent"
                                bg="transparent"
                                mb="0.5rem"
                                _hover={{
                                    bg: 'rgba(255, 255, 255, 0.08)',
                                }}>
                                <SettingsIcon
                                    onClick={handleToggleSettings}
                                    style={{
                                        transform: showSettings ? 'rotate(30deg)' : 'none', // Rotate the settings icon
                                        transition: 'transform 0.3s ease', // Adding transition effect
                                    }}
                                />
                            </Button>
                            {!isEditMode && (
                                <Button
                                    maxW="20px"
                                    minW="20px"
                                    w="25px"
                                    p="0"
                                    mr="0.4rem"
                                    maxH="20px"
                                    h="25px"
                                    ml="0.4rem"
                                    rounded="4px"
                                    colorScheme="transparent"
                                    bg="transparent"
                                    mb="0.5rem"
                                    _hover={{
                                        bg: 'rgba(255, 255, 255, 0.08)',
                                    }}>
                                    <PictureInPictureIcon onClick={handlePipToggle} />
                                </Button>
                            )}
                            {!device.isMobile && !isEditMode && (
                                <Button
                                    maxW="30px"
                                    minW="30px"
                                    w="30px"
                                    p="0"
                                    mr="0.4rem"
                                    maxH="20px"
                                    h="30px"
                                    ml="0.4rem"
                                    rounded="4px"
                                    colorScheme="transparent"
                                    bg="transparent"
                                    mb="0.5rem"
                                    _hover={{
                                        bg: 'rgba(255, 255, 255, 0.08)',
                                    }}
                                    onClick={onTheatreModePressed}>
                                    <TheatreModeIcon onClick={onTheatreModePressed} />
                                </Button>
                            )}

                            {!isEditMode && (
                                <Button
                                    maxW="25px"
                                    minW="25px"
                                    w="25px"
                                    p="0"
                                    mr="0.4rem"
                                    maxH="20px"
                                    h="25px"
                                    rounded="4px"
                                    colorScheme="transparent"
                                    bg="transparent"
                                    mb="0.5rem"
                                    ml="0.4rem"
                                    _hover={{
                                        bg: 'rgba(255, 255, 255, 0.08)',
                                    }}>
                                    {!isFullScreen ? (
                                        <FullscreenIcon onClick={handleFullScreenToggle} />
                                    ) : (
                                        <ShrinkIcon onClick={handleFullScreenToggle} />
                                    )}
                                </Button>
                            )}
                        </Flex>
                    </Flex>
                )}
            </Flex>
        </Flex>
    );

    // const additionalParam = project && project?.project_format && project.project_format === 'act' ? `&type=act` : '';

    // Add a key effect to handle video source changes
    useEffect(() => {
        // Reset player state when source changes
        if (videoRef.current) {
            // Stop any current playback
            videoRef.current.pause();

            // Reset player states
            setIsPlaying(false);
            setIsPaused(true);
            setIsBuffering(false);
            setIsWaiting(true);
            setElapsedSec(1);
            setLastTime(0);

            // Reset progress UI
            if (progressRef.current) {
                progressRef.current.style.width = '0%';
            }
            if (bufferRef.current) {
                bufferRef.current.style.width = '0%';
            }

            // Force reload video element
            videoRef.current.load();

            // Autoplay if needed
            if (autoPlay) {
                videoRef.current
                    .play()
                    .then(() => {
                        setIsPlaying(true);
                        setIsPaused(false);
                        setIsWaiting(false);
                    })
                    .catch(error => {
                        console.error('Autoplay error:', error);
                        SentryCapture(`Video autoplay error: ${error.message}`, 'error');
                    });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSource, project?.project]); // Re-run when video source changes

    const loadVideo = () => {
        setIsLoading(true);

        if (!videoRef.current) {
            return;
        }

        let videoElement = videoRef.current;

        if (videoElement && selectedSource) {
            try {
                videoElement.load();
                videoElement.addEventListener('error', (e: any) => {
                    console.error('Video load error:', e);
                    if (onLoadFailure) {
                        onLoadFailure();
                    }
                });
            } catch (error) {
                console.error('Error loading video:', error);
                if (onLoadFailure) {
                    onLoadFailure();
                }
            }
        }
    };

    return (
        <MetaTags
            description={project?.description as string}
            title={(project?.title as string) + ' - Thspian'}
            thumbnailUrl={project?.feature_image as string}>
            {project && project?.media_type === 'picture' ? (
                <Flex justifyContent="center" alignItems="center">
                    <Zoom>
                        <Image
                            src={project.project}
                            alt="Project image"
                            style={{ maxWidth: '100%', height: 'auto' }}
                            maxH={{ base: '100vh', md: '60vh' }}
                        />
                    </Zoom>
                </Flex>
            ) : (
                <Flex
                    flexDir="column"
                    cursor={showControls ? 'pointer' : 'none'}
                    align="center"
                    id="wrapper"
                    justify="center"
                    pos="relative"
                    rounded="10px"
                    overflow="hidden"
                    onMouseMove={handleControlsVisibility}
                    _hover={{
                        '.timeline-container': {
                            opacity: 1,
                        },
                    }}>
                    <ShareModal
                        isOpen={showShareModal!}
                        onClose={() => {
                            dispatch(
                                setShareModalStatus({
                                    status: false,
                                    url: ``,
                                    title: '',
                                }),
                            );
                        }}
                        shareUrl={`${SITE_URL}/watch-share?id=${project?.video_id}`}
                        title={project?.title as string}
                    />
                    {(isLoading || isBuffering) && <Spinner pos="absolute" color="white" size="xl" w={50} h={50} />}
                    {error && (
                        <>
                            <Text
                                pos="absolute"
                                color="white"
                                style={{
                                    font: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].true_white,
                                    fontSize: 13,
                                }}>
                                {loadTimeout
                                    ? 'Video failed to play. The source may be unavailable or in an unsupported format.'
                                    : 'Failed to load video, please refresh the player.'}
                            </Text>
                            <Flex pos="absolute" top={'52%'} gap="10px" justifyContent="center">
                                <Text
                                    color="white"
                                    style={{
                                        font: FONT_ENUM.REGULAR_HEADER,
                                        color: 'white',
                                        backgroundColor: 'grey',
                                        fontSize: 13,
                                        paddingRight: 10,
                                        paddingLeft: 10,
                                        paddingTop: 4,
                                        paddingBottom: 4,
                                        cursor: 'pointer',
                                        marginTop: 10,
                                        zIndex: 999,
                                    }}
                                    onClick={handleRefresh}>
                                    Refresh
                                </Text>
                                {loadTimeout && (
                                    <Text
                                        color="white"
                                        style={{
                                            font: FONT_ENUM.REGULAR_HEADER,
                                            color: 'black',
                                            backgroundColor: 'tomato',
                                            fontSize: 13,
                                            paddingRight: 10,
                                            paddingLeft: 10,
                                            paddingTop: 4,
                                            paddingBottom: 4,
                                            cursor: 'pointer',
                                            marginTop: 10,
                                            zIndex: 999,
                                        }}
                                        onClick={() => {
                                            setLoadTimeout(false);
                                            setError(false);
                                            setIsLoading(true);
                                            loadVideo();
                                        }}>
                                        Try Again
                                    </Text>
                                )}
                            </Flex>
                        </>
                    )}
                    <Flex
                        pos="absolute"
                        left="50%"
                        transform="translateX(-50%)"
                        w="full"
                        height="full"
                        align="center"
                        justify="center"
                        zIndex={9999} // Higher than InformationDisplay
                        pointerEvents="none">
                        <Flex
                            pointerEvents="auto"
                            onClick={e => handlePlayPauseClick(e)}
                            opacity={showControls ? 1 : 0}
                            transition="opacity 0.3s ease">
                            {!isPlaying ? (
                                <EsPlayButton />
                            ) : (
                                <PauseIcon
                                    height={80}
                                    width={80}
                                    style={{
                                        backgroundColor: 'rgba(169, 169, 169, 0.5)',
                                        borderRadius: '50%',
                                        padding: 4,
                                    }}
                                />
                            )}
                        </Flex>
                    </Flex>

                    {!!project && isFullScreen && isPaused && !isPlaying && (
                        <InformationDisplay themeStyle={themeStyle} project={project} />
                    )}

                    <LazyLoadComponent onLoad={handlOnLoad}>
                        <Video
                            key={`video-${project?._id || 'no-project'}-${
                                project?.project || selectedSource
                            }-${selectedResolution}-${hasFallenBackToProjectUrl}`}
                            autoPlay={autoPlay}
                            muted={systemMute}
                            src={selectedSource}
                            onClick={handlePlayPauseClick}
                            webkit-playsinline={endlessPlayer}
                            playsInline={endlessPlayer}
                            ref={videoRef}
                            loop={loop}
                            poster={customPoster ?? project?.thumbnail ?? project?.feature_image}
                            controls={false}
                            preload={selectedResolution}
                            onEnded={onEnded}
                            onError={handleError}
                            style={{
                                width: 'auto',
                                height: 'auto',
                                maxWidth: '3840px', // 4K
                                maxHeight: isFullScreen ? '100vh' : '520px',
                                objectFit: 'contain',
                                minHeight: '480px',
                                ...videoStyle,
                                ...(error && { minHeight: '480px' }),
                            }}
                        />
                    </LazyLoadComponent>

                    {!commentOpen && customControl}
                </Flex>
            )}
        </MetaTags>
    );
});

export default Player;
